import { useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { MARTINUS_ABAC_PAYLOAD, type MartinusAbacPayload } from '../abac-payloads/MartinusAbacPayload.js';
import { MartinusAccessController } from '../api/MartinusAccessController.js';
import { hashAbacPayload, hashUserPermissionFields } from '../hashUserPermissionFields.js';

/**
 * Reactive permission check hook for functional-lit components.
 *
 * Uses useQuery to call the async can() method.
 * Pass additional deps to trigger re-evaluation (e.g., when user changes).
 *
 * Dep hashing only considers fields relevant to permission evaluation (see
 * `UserWithPermissionFields`). Changes to unrelated fields on user-shaped payload
 * values (e.g. name, address) will not retrigger a permission re-evaluation.
 */
export function useMartinusABAC<P extends MartinusAbacPayload = MartinusAbacPayload>(
  payload: Omit<P, 'currentUser' | 'type'>,
  deps: unknown[] = [],
) {
  const user = useObservable(() => currentUser(), []);

  const { value, loading, error } = useQuery(
    () =>
      MartinusAccessController.evaluate({
        currentUser: user as any,
        type: MARTINUS_ABAC_PAYLOAD,
        ...payload,
      }),
    [
      MartinusAccessController,
      hashAbacPayload(payload as Record<string, unknown>),
      hashUserPermissionFields(user as Parameters<typeof hashUserPermissionFields>[0]),
      ...deps,
    ],
  );

  return {
    can: value?.allowed ?? false,
    reason: value?.reason,
    denialReasons: value?.denialReasons,
    error: error,
    loading: loading,
  };
}

export function canMartinusAbac<P extends MartinusAbacPayload = MartinusAbacPayload>(
  payload: Omit<P, 'type'>,
): Promise<boolean> {
  return MartinusAccessController.can({
    type: MARTINUS_ABAC_PAYLOAD,
    ...payload,
  });
}
